import React, { useState } from 'react';
import { createCheckoutSession, CREDIT_PACKAGES } from '../../utils/creditsApi';
import { logger } from '../../utils/logger';
import './CreditsModal.css';

/**
 * Modal for purchasing credits
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {Function} props.onClose - Handler to close modal
 * @param {Object} props.user - Firebase user object
 * @param {number|null} props.currentCredits - User's current credit balance
 */
export default function CreditsModal({ isOpen, onClose, user, currentCredits }) {
  const [selectedPackage, setSelectedPackage] = useState('credits_250');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handlePurchase = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const { url } = await createCheckoutSession(user, selectedPackage);
      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (err) {
      logger.error('Checkout error:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="credits-modal-overlay" onClick={handleOverlayClick}>
      <div className="credits-modal">
        <button className="credits-modal-close" onClick={onClose} aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>

        <div className="credits-modal-header">
          <h2>💎 Get More Gems 💎</h2>
          <p className="credits-modal-balance">
            Current balance: <strong>{currentCredits ?? 0} gems</strong>
          </p>
        </div>

        {error && (
          <div className="credits-modal-error">
            {error}
          </div>
        )}

        <div className="credits-modal-packages">
          {CREDIT_PACKAGES.map((pkg) => (
            <button
              key={pkg.id}
              className={`credits-package ${selectedPackage === pkg.id ? 'selected' : ''}`}
              onClick={() => setSelectedPackage(pkg.id)}
              disabled={loading}
            >
              {pkg.badge && <span className="credits-package-badge">{pkg.badge}</span>}
              <span className="credits-package-amount">{pkg.credits}</span>
              <span className="credits-package-label">gems</span>
              <span className="credits-package-price">{pkg.label}</span>
              <span className="credits-package-unit">
                ${(pkg.price / pkg.credits * 100).toFixed(1)}c each
              </span>
            </button>
          ))}
        </div>

        <div className="credits-modal-actions">
          <button
            className="credits-action-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="credits-action-primary"
            onClick={handlePurchase}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="credits-button-spinner"></span>
                Processing...
              </>
            ) : (
              'Continue to Payment'
            )}
          </button>
        </div>

        <p className="credits-modal-note">
          Secure payment powered by Stripe. Gems never expire. Gems are not refundable.
        </p>
      </div>
    </div>
  );
}
